import { createRoute, useNavigate } from "@tanstack/solid-router";
import { createSignal, onMount, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import { rootRoute } from "@/routes/__root";
import { AppBackground } from "@/components/app-background";
import { writeSessionToken } from "@/lib/api";
import { isTauriRuntime } from "@/lib/platform";
import { finishSocialLogin, socialErrorMessage } from "@/lib/social-login";
import { parseSocialCallbackParams } from "@/lib/social-callback-url";
import { useI18n } from "@/providers/i18n-provider";
import { showToast } from "@/components/ui/toast";
import { AlertCircle, Loader2 } from "@fleet/icons";

const ERROR_REDIRECT_DELAY_MS = 2500;

function SocialCallbackPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [failed, setFailed] = createSignal<string | null>(null);

  onMount(() => {
    const params = parseSocialCallbackParams(globalThis.window.location.hash);
    // Clears the fragment so the token doesn't linger in the address bar.
    globalThis.window.history.replaceState(null, "", globalThis.window.location.pathname);

    if (params.status !== "ok") {
      const code = params.error ?? "unknown";
      setFailed(socialErrorMessage(t, code));
      showToast(socialErrorMessage(t, code), "error");
      // Back to /login after a brief moment so no hung screen remains.
      globalThis.setTimeout(() => {
        void navigate({ to: "/login", replace: true });
      }, ERROR_REDIRECT_DELAY_MS);
      return;
    }

    // In WebView/Tauri the SameSite=Lax cookie is lost: we persist the token
    // (Bearer). On web, auth goes through the `fieldcheck.device` cookie the server
    // just set in the callback, so the `#token` fragment is ignored.
    // NOTE: native OAuth now returns via an App/Universal Link handled in
    // `deep-linking-provider.tsx` (token stored in Rust, never in JS); this
    // in-WebView `#token` path remains for web + as a Tauri fallback.
    const onTauri = isTauriRuntime();
    if (params.token && onTauri) {
      writeSessionToken(params.token);
    }

    // Tauri only: the opaque session token from the redirect fragment (`#token=…`)
    // is the Bearer it must use (the OAuth account is not in the registry yet, and
    // the `get-session` body never carries the token). Web leaves this undefined —
    // its `get-session` authenticates via the just-set device + session cookie.
    const oauthToken = onTauri ? params.token || undefined : undefined;
    void finishSocialLogin({ navigate, redirect: params.redirect, oauthToken });
  });

  return (
    <>
      {/* Decorative fixed backdrop. NEVER the scroll parent (kit #13). */}
      <AppBackground />

      {/* Own scroll container so a tall message is reachable on small viewports.
          Bound to the viewport (`h-[100dvh]`) because global CSS pins
          `html, body { overflow: hidden; height: 100% }` — a min-height container
          would be clipped instead of scrolling. */}
      <div
        class="relative z-10 flex h-[100dvh] w-full items-center justify-center overflow-x-hidden overflow-y-auto p-4"
        style={{
          "padding-top": "max(1rem, env(safe-area-inset-top))",
          "padding-bottom": "max(1rem, env(safe-area-inset-bottom))",
        }}
      >
        <div class="flex w-full max-w-sm flex-col items-center justify-center gap-4 text-center">
          <Show
            when={failed()}
            fallback={
              <>
                <Loader2 class="h-8 w-8 animate-spin text-muted-foreground" />
                <p class="text-sm text-muted-foreground">{t(msg`Completing sign-in…`)}</p>
              </>
            }
          >
            {(message) => (
              <>
                <AlertCircle class="h-8 w-8 text-destructive" />
                <p class="text-sm text-destructive">{message()}</p>
                <p class="text-xs text-muted-foreground">{t(msg`Redirecting to sign in…`)}</p>
              </>
            )}
          </Show>
        </div>
      </div>
    </>
  );
}

export const socialCallbackRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/auth/social/callback",
  component: SocialCallbackPage,
});
